import { useState } from "react";
import { Wrench } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import CrudPage from "@/components/CrudPage";
import { Pill, Field } from "@/components/kit";
import { Photos } from "@/components/PhotoPicker";
import { api, errMsg, fmtD, todayISO, addDaysISO } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const EQ_TYPES = ["fridge", "freezer", "hot_holding", "oven", "probe", "dishwasher", "ice_machine", "other"].map((v) => ({ value: v, label: v.replace("_", " ") }));

function ServiceButton({ row, reload }) {
  const [f, setF] = useState(null);
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/equipment/${row.id}/service`, { date: f.date, notes: f.notes, next_due: f.next_due || null });
      toast.success("Service logged");
      setF(null);
      reload();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <>
      <button data-testid={`service-equipment-${row.id}`} onClick={() => setF({ date: todayISO(), notes: "", next_due: "" })} title="Log service" className="ka-press rounded-md p-2 text-slate-500 hover:bg-emerald-50 hover:text-emerald-700"><Wrench className="h-4 w-4" /></button>
      <Dialog open={!!f} onOpenChange={(o) => !o && setF(null)}>
        <DialogContent className="text-left sm:max-w-md">
          <DialogHeader><DialogTitle className="font-display text-2xl">Log service · {row.name}</DialogTitle></DialogHeader>
          {f && (
            <form onSubmit={save} className="space-y-4">
              <Field label="Service date"><input data-testid="service-date-input" type="date" required className="ka-input" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} /></Field>
              <Field label="Engineer notes"><textarea data-testid="service-notes-input" rows={3} className="ka-input" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></Field>
              <Field label="Next service due"><input data-testid="service-next-input" type="date" className="ka-input" value={f.next_due} onChange={(e) => setF({ ...f, next_due: e.target.value })} /></Field>
              <button data-testid="save-service-button" className="ka-press w-full rounded-full bg-emerald-600 py-3 text-sm font-semibold text-white hover:bg-emerald-700">Save service record</button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

const limits = (r) => [r.min_temp != null && `≥ ${r.min_temp}°C`, r.max_temp != null && `≤ ${r.max_temp}°C`].filter(Boolean).join(" · ") || "Default";

export function Equipment() {
  const { can } = useAuth();
  const today = todayISO();
  return (
    <CrudPage eyebrow="Register" title="Equipment" resource="equipment" venueScoped
      subtitle="Every fridge, freezer and hot-hold unit with its own temperature limits and service history."
      defaults={{ name: "", type: "fridge", location: "", min_temp: null, max_temp: null, service_due: "", status: "in_use", notes: "" }}
      columns={[
        { key: "name", label: "Equipment", render: (r) => <div><div className="font-semibold text-slate-900">{r.name}</div><div className="text-xs text-slate-500">{r.location}</div></div> },
        { key: "type", label: "Type", render: (r) => <span className="capitalize">{r.type.replace("_", " ")}</span> },
        { key: "limits", label: "Limits", render: limits },
        { key: "service_due", label: "Service due", render: (r) => r.service_due ? <Pill status={r.service_due < today ? "overdue" : "ok"} label={fmtD(r.service_due)} /> : "—" },
        { key: "status", label: "Status", render: (r) => <Pill status={r.status === "in_use" ? "ok" : "overdue"} label={r.status.replace(/_/g, " ")} /> },
        { key: "history", label: "Services", render: (r) => (r.service_history || []).length },
      ]}
      fields={[
        { key: "name", label: "Name", required: true },
        { key: "type", label: "Type", type: "select", options: EQ_TYPES },
        { key: "location", label: "Location" },
        { key: "status", label: "Status", type: "select", options: [{ value: "in_use", label: "In use" }, { value: "faulty", label: "Faulty" }, { value: "out_of_service", label: "Out of service" }] },
        { key: "min_temp", label: "Minimum °C", type: "number", hint: "Leave blank to use your business limits" },
        { key: "max_temp", label: "Maximum °C", type: "number", hint: "Leave blank to use your business limits" },
        { key: "service_due", label: "Next service due", type: "date" },
        { key: "notes", label: "Notes", type: "textarea" },
      ]}
      rowActions={can("manage") ? (row, reload) => <ServiceButton row={row} reload={reload} /> : undefined}
    />
  );
}

export function Training() {
  const today = todayISO();
  const soon = addDaysISO(today, 30);
  const status = (r) => !r.expires_on ? "ok" : r.expires_on < today ? "overdue" : r.expires_on <= soon ? "exception" : "ok";
  return (
    <CrudPage eyebrow="Staff" title="Training" resource="training"
      subtitle="Induction, Level 2 food hygiene, allergen awareness and other certificates. Owners and managers get an alert 30 days before one expires."
      defaults={{ staff_name: "", course: "Level 2 Food Hygiene", completed_on: today, expires_on: "", certificate_ref: "", notes: "", photo_ids: [] }}
      columns={[
        { key: "staff_name", label: "Staff member", render: (r) => <span className="font-semibold text-slate-900">{r.staff_name}</span> },
        { key: "course", label: "Course" },
        { key: "completed_on", label: "Completed", render: (r) => fmtD(r.completed_on) },
        { key: "expires_on", label: "Expires", render: (r) => r.expires_on ? <Pill status={status(r)} label={`${status(r) === "overdue" ? "Expired " : status(r) === "exception" ? "Due " : ""}${fmtD(r.expires_on)}`} /> : "No expiry" },
        { key: "certificate_ref", label: "Certificate", render: (r) => <div>{r.certificate_ref || "—"}{!!r.photo_ids?.length && <div className="mt-1"><Photos ids={r.photo_ids} size="h-10 w-10" /></div>}</div> },
      ]}
      fields={[
        { key: "staff_name", label: "Staff member", required: true },
        { key: "course", label: "Course", required: true },
        { key: "completed_on", label: "Completed on", type: "date", required: true },
        { key: "expires_on", label: "Expires on", type: "date" },
        { key: "certificate_ref", label: "Certificate number / reference" },
        { key: "photo_ids", label: "Certificate photo or scan", type: "photos" },
        { key: "notes", label: "Notes", type: "textarea" },
      ]}
    />
  );
}

export function Suppliers() {
  return (
    <CrudPage eyebrow="Register" title="Suppliers" resource="suppliers"
      subtitle="Your approved supplier list. Deliveries are checked against it."
      defaults={{ name: "", contact_name: "", phone: "", email: "", category: "", approved: true, notes: "" }}
      columns={[
        { key: "name", label: "Supplier", render: (r) => <span className="font-semibold text-slate-900">{r.name}</span> },
        { key: "category", label: "Category" },
        { key: "contact_name", label: "Contact", render: (r) => <div>{r.contact_name || "—"}<div className="text-xs text-slate-500">{r.phone} {r.email}</div></div> },
        { key: "approved", label: "Status", render: (r) => <Pill status={r.approved ? "ok" : "overdue"} label={r.approved ? "Approved" : "Not approved"} /> },
      ]}
      fields={[
        { key: "name", label: "Supplier name", required: true },
        { key: "category", label: "Category", placeholder: "Meat, dairy, produce…" },
        { key: "contact_name", label: "Contact name" },
        { key: "phone", label: "Phone" },
        { key: "email", label: "Email" },
        { key: "approved", label: "Approved supplier", type: "switch", on: "Approved", off: "Not approved" },
        { key: "notes", label: "Notes", type: "textarea" },
      ]}
    />
  );
}
